import React, { useState, useCallback } from 'react';
import T from 'i18n';
import { parse, isValid, startOfDay, endOfDay, format as fnsFormat } from 'date-fns';

const date = new Date();
export const defaultFilter = { from: startOfDay(date), to: endOfDay(date) };

const TIME_FORMAT = 'hh:mm';
const FULL_TIME_FORMAT = 'hh:mm aa';

const formatTimeOnly = (d) => {
  if (!d || !isValid(d)) return '';
  let h = d.getHours() % 12 || 12;
  const min = d.getMinutes().toString().padStart(2, '0');
  return `${h.toString().padStart(2, '0')}:${min}`;
};

const getPeriod = (d) => {
  if (!d || !isValid(d)) return 'AM';
  return d.getHours() >= 12 ? 'PM' : 'AM';
};

const parseTime = (timeStr, period) => {
  const trimmed = timeStr.trim();
  if (!trimmed) return null;
  const parsed = parse(`${trimmed} ${period}`, FULL_TIME_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

const isTimeValid = (value) => {
  if (!value.trim()) return true;
  return isValid(parse(value.trim(), TIME_FORMAT, new Date()));
};

const buildDateWithTime = (baseDate, timeParsed) => {
  if (!timeParsed) return baseDate;
  const result = new Date(baseDate);
  result.setHours(timeParsed.getHours(), timeParsed.getMinutes(), timeParsed.getSeconds(), 0);
  return result;
};

const FilterPresentedDocuments = React.memo(({ documentsFilter, setDocumentsFilter }) => {
  const [fromTime, setFromTime] = useState(() => formatTimeOnly(documentsFilter.from));
  const [fromPeriod, setFromPeriod] = useState(() => getPeriod(documentsFilter.from));
  const [toTime, setToTime] = useState(() => formatTimeOnly(documentsFilter.to));
  const [toPeriod, setToPeriod] = useState(() => getPeriod(documentsFilter.to));

  const fromValid = isTimeValid(fromTime);
  const toValid = isTimeValid(toTime);
  const allValid = fromValid && toValid;
  const hasInput = fromTime.trim() !== '' || toTime.trim() !== '';
  const canSubmit = allValid && hasInput;

  const handleApply = useCallback(() => {
    if (!allValid) return;

    const today = new Date();
    const fromParsed = fromTime.trim() ? parseTime(fromTime, fromPeriod) : null;
    const toParsed = toTime.trim() ? parseTime(toTime, toPeriod) : null;

    if ((fromTime.trim() && !fromParsed) || (toTime.trim() && !toParsed)) return;

    const from = fromParsed ? buildDateWithTime(startOfDay(today), fromParsed) : startOfDay(today);
    const to = toParsed ? buildDateWithTime(startOfDay(today), toParsed) : endOfDay(today);

    setDocumentsFilter({ from, to });
  }, [allValid, fromTime, fromPeriod, toTime, toPeriod, setDocumentsFilter]);

  const handleClear = useCallback(() => {
    setFromTime(formatTimeOnly(defaultFilter.from));
    setFromPeriod(getPeriod(defaultFilter.from));
    setToTime(formatTimeOnly(defaultFilter.to));
    setToPeriod(getPeriod(defaultFilter.to));
    setDocumentsFilter(defaultFilter);
  }, [setDocumentsFilter]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && canSubmit) {
        e.preventDefault();
        handleApply();
      }
    },
    [canSubmit, handleApply],
  );

  return (
    <div className="rt-comments-filter" onKeyDown={handleKeyDown}>
      <div className="rt-comments-filter__header">
        <span className="rt-comments-filter__title">
          {T.translate('filters.filters')}
        </span>
        <button
          type="button"
          className="rt-comments-filter__clear-btn"
          onClick={handleClear}
        >
          {T.translate('filters.clear')}
        </button>
      </div>

      <div className="rt-comments-filter__section">
        <label className="rt-comments-filter__label">
          {T.translate('generic.from')}
        </label>
        <div className="rt-comments-filter__row">
          <input
            type="text"
            className={`rt-comments-filter__input rt-comments-filter__input--time ${!fromValid ? 'is-invalid' : ''}`}
            placeholder="hh:mm"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
          />
          <button
            type="button"
            className="rt-comments-filter__period-toggle"
            onClick={() => setFromPeriod((p) => (p === 'AM' ? 'PM' : 'AM'))}
          >
            <span className={`rt-comments-filter__period-option ${fromPeriod === 'AM' ? 'is-active' : ''}`}>AM</span>
            <span className={`rt-comments-filter__period-option ${fromPeriod === 'PM' ? 'is-active' : ''}`}>PM</span>
          </button>
        </div>
      </div>

      <div className="rt-comments-filter__section">
        <label className="rt-comments-filter__label">
          {T.translate('generic.to')}
        </label>
        <div className="rt-comments-filter__row">
          <input
            type="text"
            className={`rt-comments-filter__input rt-comments-filter__input--time ${!toValid ? 'is-invalid' : ''}`}
            placeholder="hh:mm"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
          />
          <button
            type="button"
            className="rt-comments-filter__period-toggle"
            onClick={() => setToPeriod((p) => (p === 'AM' ? 'PM' : 'AM'))}
          >
            <span className={`rt-comments-filter__period-option ${toPeriod === 'AM' ? 'is-active' : ''}`}>AM</span>
            <span className={`rt-comments-filter__period-option ${toPeriod === 'PM' ? 'is-active' : ''}`}>PM</span>
          </button>
        </div>
      </div>

      <div className="rt-comments-filter__actions">
        <button
          type="button"
          className="rt-comments-filter__apply-btn"
          disabled={!canSubmit}
          onClick={handleApply}
        >
          {T.translate('generic.apply', { defaultValue: 'Apply' })}
        </button>
      </div>
    </div>
  );
});

export default FilterPresentedDocuments;
